import { useCallback, useEffect, useState } from 'react';
import {
  createBranch,
  createVersion,
  listBranches,
  listVersions,
  restoreVersion,
  type BranchRow,
  type VersionListItem,
} from '../../data/versionRepo';
import { VERSION_STATUS_META } from '../../data/types';
import { toHash } from '../../boot/router';
import { useUserDirectory } from '../../hooks/useUserDirectory';

/**
 * 버전 패널 (작업지시서 P4-08)
 *
 * ── 화면이 반드시 전달해야 하는 것 ──────────────────────────────────────
 *
 *   "되돌리면 지금 것이 사라지나?" 에 대한 답.
 *   실제로는 사라지지 않는다(새 버전으로 붙는다). 그런데 화면이 그걸
 *   말해주지 않으면 사용자는 무서워서 복원 버튼을 안 누른다.
 *   → 확인 문구에 "지금 버전은 그대로 남습니다" 를 명시한다.
 */

export interface VersionPanelProps {
  drawingId: string;
  currentVersionId: string;
  currentBranchId: string;
  /** 버전이 새로 생겨 화면을 갈아끼워야 할 때 */
  onNavigate: (versionId: string) => void;
}

export function VersionPanel({
  drawingId,
  currentVersionId,
  currentBranchId,
  onNavigate,
}: VersionPanelProps) {
  const { nameOf } = useUserDirectory();
  const [versions, setVersions] = useState<VersionListItem[]>([]);
  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [branchingFrom, setBranchingFrom] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<VersionListItem | null>(null);

  const reload = useCallback(async () => {
    try {
      const [v, b] = await Promise.all([listVersions(drawingId), listBranches(drawingId)]);
      setVersions(v);
      setBranches(b);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [drawingId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** 실패 이유를 삼키지 않는다 — 저장 실패를 모르면 작업이 날아간다 */
  async function run(what: string, fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await reload();
    } catch (e) {
      setError(`${what} 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  const branchName = (id: string) => branches.find((b) => b.id === id)?.name ?? '?';

  return (
    <div className="ver-panel">
      {error && (
        <p className="cad-panel__notice cad-panel__notice--error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="cad-panel__notice" role="status">
          {notice}
        </p>
      )}

      <div className="ver-panel__actions">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={busy}
          onClick={() =>
            run('버전 저장', async () => {
              const v = await createVersion({
                drawingId,
                branchId: currentBranchId,
                parentVersionId: currentVersionId,
                changeSummary: '수동 저장',
              });
              setNotice(`${v.name} 을 만들었습니다.`);
              onNavigate(v.id);
            })
          }
        >
          현재 상태로 새 버전
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={busy}
          onClick={() => setBranchingFrom(currentVersionId)}
        >
          여기서 분기 (A안/B안)
        </button>
      </div>

      {branchingFrom && (
        <BranchForm
          busy={busy}
          onCancel={() => setBranchingFrom(null)}
          onSubmit={(name) =>
            run('분기 생성', async () => {
              const { branch, version } = await createBranch(drawingId, branchingFrom, name);
              setBranchingFrom(null);
              setNotice(`'${branch.name}' 안을 만들었습니다.`);
              onNavigate(version.id);
            })
          }
        />
      )}

      <p className="cad-panel__hint">
        비교할 버전을 고르면 지금 버전과 무엇이 달라졌는지 보여줍니다.
      </p>

      <ol className="ver-list">
        {versions.map((v) => {
          const meta = VERSION_STATUS_META[v.status];
          const isCurrent = v.id === currentVersionId;
          return (
            <li key={v.id} className={isCurrent ? 'ver-row ver-row--current' : 'ver-row'}>
              <div className="ver-row__head">
                <span className="ver-row__num">{v.name || `v${v.version_number}`}</span>
                <span className="ver-row__branch">{branchName(v.branch_id)}</span>
                <span className="ver-row__status" style={{ color: meta.color }}>
                  <span aria-hidden="true">{meta.icon}</span> {meta.label}
                </span>
              </div>

              <div className="ver-row__meta">
                {v.change_summary && <span className="ver-row__summary">{v.change_summary}</span>}
                <span className="ver-row__when">
                  {new Date(v.created_at).toLocaleString('ko-KR')} ·{' '}
                  <span title={v.created_by}>{nameOf(v.created_by)}</span> · 객체 {v.entity_count}
                </span>
              </div>

              <div className="ver-row__buttons">
                {!isCurrent && (
                  <>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => onNavigate(v.id)}
                    >
                      열기
                    </button>
                    <a
                      className="btn btn--ghost btn--sm"
                      href={toHash({ name: 'compare', drawingId, a: v.id, b: currentVersionId })}
                    >
                      지금과 비교
                    </a>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={busy}
                      onClick={() => setRestoring(v)}
                    >
                      이 버전으로 되돌리기
                    </button>
                  </>
                )}
                {isCurrent && <span className="ver-row__here">지금 보는 버전</span>}
              </div>

              {restoring?.id === v.id && (
                <RestoreConfirm
                  version={v}
                  busy={busy}
                  onCancel={() => setRestoring(null)}
                  onConfirm={() =>
                    run('되돌리기', async () => {
                      const label = v.name || `v${v.version_number}`;
                      const made = await restoreVersion(
                        drawingId,
                        currentBranchId,
                        v.id,
                        label
                      );
                      setRestoring(null);
                      setNotice(`${label} 내용으로 ${made.name} 을 만들었습니다.`);
                      onNavigate(made.id);
                    })
                  }
                />
              )}
            </li>
          );
        })}
      </ol>

      {versions.length === 0 && !error && <p className="cad-panel__hint">버전이 없습니다.</p>}
    </div>
  );
}

function BranchForm({
  busy,
  onSubmit,
  onCancel,
}: {
  busy: boolean;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  return (
    <form
      className="layer-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(name);
      }}
    >
      <label className="param-row">
        <span className="param-row__label">안 이름</span>
        <input
          className="input"
          value={name}
          autoFocus
          placeholder="B안 — 신선 확장"
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <p className="cad-panel__hint">
        지금 버전을 복사해 새 안을 만듭니다. 원래 안은 그대로 남습니다.
      </p>
      <div className="layer-form__actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !name.trim()}>
          만들기
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
          취소
        </button>
      </div>
    </form>
  );
}

/**
 * 복원 확인.
 *
 * ★ "지금 버전은 그대로 남습니다" 를 반드시 보여준다.
 *   실제로 안 사라지는데 화면이 말을 안 하면 사용자는 버튼을 못 누른다.
 */
function RestoreConfirm({
  version,
  busy,
  onConfirm,
  onCancel,
}: {
  version: VersionListItem;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="layer-form">
      <p className="cad-panel__hint">
        <strong>{version.name || `v${version.version_number}`}</strong> 의 내용으로{' '}
        <strong>새 버전</strong>을 만듭니다.
        <br />
        지금 버전은 그대로 남습니다 — 언제든 다시 돌아올 수 있습니다.
      </p>
      <div className="layer-form__actions">
        <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={onConfirm}>
          되돌리기
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
          취소
        </button>
      </div>
    </div>
  );
}
